// Small presentational helpers for the supplier pricing screens.
import React from "react";
import "./supplier-pricing.scss";

type BtnKind = "primary" | "secondary" | "ghost" | "green" | "red" | "quote" | "whatsapp";

export const PBtn = ({
  kind = "primary",
  size,
  className = "",
  ...p
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { kind?: BtnKind; size?: "sm" }) => (
  <button type="button" className={`eb-pr-btn eb-pr-btn--${kind} ${size === "sm" ? "eb-pr-btn--sm" : ""} ${className}`} {...p} />
);

export const PCard = ({
  title,
  subtitle,
  actions,
  children,
  style,
}: {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  children?: React.ReactNode;
  style?: React.CSSProperties;
}) => (
  <section className="eb-pr-card" style={style}>
    {(title || actions) && (
      <div className="eb-pr-between">
        <div>
          {title && <h3>{title}</h3>}
          {subtitle && <p className="eb-pr-sub">{subtitle}</p>}
        </div>
        {actions && <div className="eb-pr-row">{actions}</div>}
      </div>
    )}
    {children}
  </section>
);

export const PField = ({ label, children, style }: { label?: React.ReactNode; children: React.ReactNode; style?: React.CSSProperties }) => (
  <div className="eb-pr-field" style={style}>
    {label && <span className="eb-pr-label">{label}</span>}
    {children}
  </div>
);

export const PNumber = ({
  value,
  onChange,
  suffix,
  placeholder = "—",
  size,
  warn,
  width,
  disabled,
  ariaLabel,
}: {
  value: number | string | null | undefined;
  onChange: (v: string) => void;
  suffix?: React.ReactNode;
  placeholder?: string;
  size?: "sm";
  warn?: boolean;
  width?: number;
  disabled?: boolean;
  ariaLabel?: string;
}) => (
  <span className={`eb-pr-input ${size === "sm" ? "eb-pr-input--sm" : ""} ${warn ? "eb-pr-input--warn" : ""}`} style={{ width }}>
    <input
      type="number"
      inputMode="decimal"
      step="any"
      aria-label={ariaLabel}
      value={value === null || value === undefined ? "" : value}
      placeholder={placeholder}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    />
    {suffix && <span className="eb-pr-suffix">{suffix}</span>}
  </span>
);

export const PText = ({
  value,
  onChange,
  width,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  width?: number;
  placeholder?: string;
}) => (
  <span className="eb-pr-input" style={{ width }}>
    <input type="text" value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
  </span>
);

export const PSelect = ({
  value,
  onChange,
  options,
  width,
  size,
  ariaLabel,
  disabled,
}: {
  value: string | number;
  onChange: (v: string) => void;
  options: { value: string | number; label: string }[];
  width?: number;
  size?: "sm";
  ariaLabel?: string;
  disabled?: boolean;
}) => (
  <span className={`eb-pr-input ${size === "sm" ? "eb-pr-input--sm" : ""}`} style={{ width }}>
    <select aria-label={ariaLabel} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  </span>
);

export const PToggle = ({ on, onChange, label }: { on: boolean; onChange: (v: boolean) => void; label: string }) => (
  <button type="button" role="switch" aria-checked={on} aria-label={label} className={`eb-pr-toggle ${on ? "on" : ""}`} onClick={() => onChange(!on)} />
);

export const PSegmented = ({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) => (
  <div className="eb-pr-seg" role="radiogroup">
    {options.map((o) => (
      <button type="button" key={o.value} role="radio" aria-checked={value === o.value} className={value === o.value ? "on" : ""} onClick={() => onChange(o.value)}>
        {o.label}
      </button>
    ))}
  </div>
);

export const PTabs = ({ value, onChange, tabs }: { value: string; onChange: (v: string) => void; tabs: { value: string; label: string }[] }) => (
  <div className="eb-pr-tabs" role="tablist">
    {tabs.map((t) => (
      <button
        type="button"
        role="tab"
        key={t.value}
        aria-selected={value === t.value}
        className={`eb-pr-tab ${value === t.value ? "eb-pr-tab--on" : ""}`}
        onClick={() => onChange(t.value)}
      >
        {t.label}
      </button>
    ))}
  </div>
);

export const PPill = ({ tone, children }: { tone?: "green" | "blue" | "amber"; children: React.ReactNode }) => (
  <span className={`eb-pr-pill ${tone ? `eb-pr-pill--${tone}` : ""}`}>{children}</span>
);

export const PRadioCard = ({ on, title, desc, onClick }: { on: boolean; title: string; desc: string; onClick: () => void }) => (
  <button type="button" className={`eb-pr-radio ${on ? "on" : ""}`} onClick={onClick} aria-pressed={on}>
    <span className="dot" />
    <span>
      <b>{title}</b>
      <small>{desc}</small>
    </span>
  </button>
);

export const fmt = (n: number | null | undefined, d = 2) =>
  Number(n || 0).toLocaleString("he-IL", { minimumFractionDigits: d, maximumFractionDigits: d });
export const fmt0 = (n: number | null | undefined) => Number(n || 0).toLocaleString("he-IL", { maximumFractionDigits: 0 });
export const fmtDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString("he-IL", { day: "2-digit", month: "2-digit", year: "numeric" }) : "";
export const toNum = (v: string) => (v === "" ? null : Number(v));

/** Excel-friendly CSV (UTF-8 BOM keeps Hebrew readable in Excel). */
export function downloadCsv(filename: string, rows: (string | number | null | undefined)[][]) {
  const csv = "﻿" + rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\r\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}
